import {
    get,
    snakeCase,
    cloneDeep,
    set,
    has,
} from 'lodash';

export function idFromTitle(title) {
    return snakeCase(title);
}

/**
 * 
 * @param { {title: string, id?: string} } options 
 */
export function generatePage(options) {
    const id = options.id || idFromTitle(options.title);
    return {
        id,
        title: options.title,
        type: "page",
        'X-Order': [],
        'X-Rules': [],
        properties: {},
        'X-Show-Label': false,
    };
}

export function hasPage(schema, page_id) {
    return has(schema, ['properties', page_id]) && schema.properties[page_id].type === 'page';
}

/**
 * 
 * @param { {title: string} } options 
 * @param {any} schema 
 */
export function addPage(options, schema) {
    const new_page = generatePage(options);
    let new_schema = cloneDeep(schema);
    // don't add multiple, we will probably catch this earlier though
    if (!new_schema.hasOwnProperty('properties')) {
        new_schema.properties = {};
    }
    
    if (new_schema.properties.hasOwnProperty(new_page.id)) {
        return schema;
    }

    if (!new_schema.hasOwnProperty('X-Order')) {
        new_schema['X-Order'] = [];
    }

    new_schema['X-Order'].push(new_page.id);
    new_schema.properties[new_page.id] = new_page;

    return new_schema;
}

/**
 * 
 * @param {Object} options options from `add_page_property_form_data`
 * @param {Object} schema full assessment schema
 * @param {string} page_id id for page to adding to˙
 * @param {function} addFn generator for property type
 */
export function baseAdd(options, schema, page_id, addFn) {
    if (!hasPage(schema, page_id)) {
        return schema;
    }
    
    const property = addFn(options);
    let new_schema = cloneDeep(schema);
    const required = get(options, ['required'], false);
    set(new_schema, ['properties', page_id, 'properties', property.id], property);
    new_schema.properties[page_id]['X-Order'] = [...(new Set(new_schema.properties[page_id]['X-Order']).add(property.id))];
    if (required) {
        new_schema.properties[page_id]['required'] = [...(new Set(new_schema.properties[page_id]['required']).add(property.id))];
    }
    return new_schema;
}

/**
 * 
 * @param { {title: string, description: string, id?: string} } options 
 */
export function generateText(options) {
    const description = get(options, 'description', '');
    const title = get(options, 'title', '');
    const id = options.id || idFromTitle(title);

    return {
        "X-Format": "description",
        "type": "text",
        "description": description,
        "title": title,
        "id": id,
    };
}

/**
 * 
 * @param { {title: string, description: string} } options text options
 * @param {any} schema assessment schema
 * @param {string} page_id page to add text type to
 */
export function addText(options, schema, page_id) {
    return baseAdd(options, schema, page_id, generateText);
}

/**
 * 
 * @param { {title: string, description: string, 'X-Format'?: string, id?: string, 'X-Encode'?: boolean} } options 
 */
export function generateString(options) {
    const description = get(options, 'description', '');
    const title = get(options, 'title', '');
    const format = get(options, 'X-Format', 'default');
    const encode = get(options, 'X-Encode', true);
    const id = options.id || idFromTitle(title);

    return {
        "type": "string",
        "description": description,
        "title": title,
        "X-Encode": encode,
        "X-Format": format,
        "id": id,
    };
}

/**
 * 
 * @param { {title: string, description: string} } options options
 * @param {any} schema assessment schema
 * @param {string} page_id page to add type to
 */
export function addString(options, schema, page_id) {
    return baseAdd(options, schema, page_id, generateString);
}

/**
 * 
 * @param {} options 
 */
export function generateRadio(options) {
    const title = get(options, 'title', '');
    const description = get(options, 'description', '');
    const radio_options = get(options, 'options', []);
    const id = options.id || idFromTitle(title);
    const format = get(options, 'format', 'default');

    return {
        "X-Options": {
            "enumOptions": radio_options.map((v) => {
                return {
                    value: v.value,
                    label: v.label,
                };
            })
          },
          "X-Format": format,
          "type": "radio",
          "title": title,
          "description": description,
          "id": id,
    };
}

/**
 * 
 * @param { {title: string, description: string} } options radio options
 * @param {any} schema assessment schema
 * @param {string} page_id page to add radio type to
 */
export function addRadio(options, schema, page_id) {
    return baseAdd(options, schema, page_id, generateRadio);    
}

/**
 * 
 * @param {} options 
 */
export function generateSurveyTable(options) {
    const title = get(options, 'title', '');
    const description = get(options, 'description', '');
    const id = options.id || idFromTitle(title);

    return {
        "X-Options": {
            "rows": []
          },
          "type": "survey_table",
          "title": title,
          "description": description,
          "id": id,
    };
}

/**
 * 
 * @param { {title: string, description: string} } options options
 * @param {any} schema assessment schema
 * @param {string} page_id page to add type to
 */
export function addSurveyTable(options, schema, page_id) {
    return baseAdd(options, schema, page_id, generateSurveyTable);    
}

/**
 * 
 * @param { {title: string, description: string, id?: string} } options 
 */
export function generateFile(options) {
    const description = get(options, 'description', '');
    const title = get(options, 'title', '');
    const id = options.id || idFromTitle(title);

    return {
        "type": "file",
        "description": description,
        "title": title,
        "id": id,
    };
}

/**
 * 
 * @param { {title: string, description: string} } options text options
 * @param {any} schema assessment schema
 * @param {string} page_id page to add text type to
 */
export function addFile(options, schema, page_id) {
    return baseAdd(options, schema, page_id, generateFile);
}

/**
 * 
 * @param {} options 
 */
export function generateBoolean(options) {
    const title = get(options, 'title', '');
    const description = get(options, 'description', '');
    const format = get(options, 'format', 'default');
    const orientation = get(options, 'orientation', 'default');
    const id = options.id || idFromTitle(title);

    return {
        "X-Options": {
            "rows": []
          },
          "type": "boolean",
          "title": title,
          "description": description,
          "id": id,
          'X-Orientation': orientation,
          'X-Format': format,
    };
}

/**
 * 
 * @param { {title: string, description: string} } options options
 * @param {any} schema assessment schema
 * @param {string} page_id page to add type to
 */
export function addBoolean(options, schema, page_id) {
    return baseAdd(options, schema, page_id, generateBoolean);    
}

export const generate_map = [
    {
        title: 'Text',
        type: 'text',
        addFn: addText,
    },
    {
        title: 'Radio',
        type: 'radio',
        addFn: addRadio,
    },
    {
        title: 'Survey Table',
        type: 'survey_table',
        addFn: addSurveyTable,
    },
    {
        title: 'Boolean',
        type: 'boolean',
        addFn: addBoolean,
    },
    {
        title: 'File',
        type: 'file',
        addFn: addFile,
    },
];



// WEBPACK FOOTER //
// ./src/utils/schemaGenerators.js